import axios from 'axios';
import {API_URL} from '../config/constants';

// Daedalus is reached through the ISAM backend proxy (/api/v2/cfr/search).
// Browsers cannot call the external Daedalus host directly in locked-down
// deployments: FAA environments enforce a Content-Security-Policy limiting
// connect-src to self and *.faa.gov.
const CFR_SEARCH_URL = `${API_URL}api/v2/cfr/search`;
const CFR_ANALYSIS_URL = `${API_URL}api/v2/cfr/analysis`;

/** Marker in the analysis prompt the backend replaces with the formatted Daedalus results. */
export const DAEDALUS_RESULTS_MARKER = '[[DAEDALUS_RESULTS]]';

/** Lifecycle phase of a backend CFR analysis run. */
export type CfrAnalysisPhase = 'SEARCHING_REGULATIONS' | 'GENERATING_ANALYSIS' | 'DONE' | 'ERROR';

/**
 * Poll snapshot of a backend CFR analysis run. The partial text grows while
 * the LLM streams; the result is set once the run completes.
 */
export interface CfrAnalysisSnapshot {
    runId: string;
    phase: CfrAnalysisPhase;
    detail: string | null;
    partialText: string;
    result: string | null;
    error: string | null;
    done: boolean;
}

/** Request body for starting a backend CFR analysis run. */
export interface CfrAnalysisRequest {
    nodeId: string;
    searchQuery: string;
    prompt: string;
    searchLimit?: number;
    searchThreshold?: number;
}

/**
 * Search result from Daedalus CFR API
 */
export interface CfrSearchResult {
    chunk: {
        id: string;
        section_id: string;
        chunk_index: number;
        content: string;
    };
    section?: {
        id: string;
        part: number;
        section: string;
        title: string;
        content: string;
        volume: number;
    };
    score: number;
}

/**
 * Service for interacting with the Daedalus CFR Expert System API
 */
class CfrService {
    /**
     * Perform semantic search across CFR regulations
     * @param query Search query text
     * @param limit Maximum number of results (default 5)
     * @param threshold Minimum similarity score 0-1 (default 0.7)
     */
    async semanticSearch(
        query: string,
        limit: number = 5,
        threshold: number = 0.6
    ): Promise<CfrSearchResult[]> {
        // eslint-disable-next-line no-console
        console.log('CFR Search - Query:', query);
        // eslint-disable-next-line no-console
        console.log('CFR Search - URL:', CFR_SEARCH_URL);

        try {
            const response = await axios.get<CfrSearchResult[]>(
                CFR_SEARCH_URL,
                {
                    params: { q: query, limit, threshold },
                    withCredentials: true
                }
            );
            // eslint-disable-next-line no-console
            console.log('CFR Search - Response:', response.data);
            return response.data;
        } catch (error: any) {
            // eslint-disable-next-line no-console
            console.error('CFR Search - Error:', error);
            // eslint-disable-next-line no-console
            console.error('CFR Search - Error Response:', error.response?.data);
            // eslint-disable-next-line no-console
            console.error('CFR Search - Error Status:', error.response?.status);
            throw error;
        }
    }

    /**
     * Search for regulations related to a fault tree node (barrier)
     * Combines node name and statement of truth for better search results
     * @param nodeName The name of the fault tree node
     * @param statementOfTruth Optional statement of truth for the node
     * @param description Optional description of the node
     */
    async searchForNodeRegulations(
        nodeName: string,
        statementOfTruth?: string,
        description?: string
    ): Promise<CfrSearchResult[]> {
        // Build a comprehensive search query from node details
        const queryParts = [nodeName];
        if (statementOfTruth) {
            queryParts.push(statementOfTruth);
        }
        if (description) {
            queryParts.push(description);
        }
        const query = queryParts.join(' ');

        return this.semanticSearch(query, 5, 0.6);
    }

    /**
     * Start an asynchronous CFR analysis run on the backend.
     * @returns the run id to poll
     */
    async startAnalysis(request: CfrAnalysisRequest): Promise<string> {
        const response = await axios.post<{ runId: string }>(CFR_ANALYSIS_URL, request, {
            withCredentials: true
        });
        return response.data.runId;
    }

    /**
     * Poll the snapshot of a CFR analysis run.
     */
    async getAnalysisSnapshot(runId: string): Promise<CfrAnalysisSnapshot> {
        const response = await axios.get<CfrAnalysisSnapshot>(`${CFR_ANALYSIS_URL}/${runId}`, {
            withCredentials: true
        });
        return response.data;
    }

    /**
     * Fetch the caller's most recent analysis run for a node, letting a client
     * that lost its run id (e.g. a page reload) resume.
     */
    async getLatestAnalysis(nodeId: string): Promise<CfrAnalysisSnapshot> {
        const response = await axios.get<CfrAnalysisSnapshot>(`${CFR_ANALYSIS_URL}/latest/${nodeId}`, {
            withCredentials: true
        });
        return response.data;
    }

    /**
     * Format search results into a readable string for LLM context
     */
    formatResultsForContext(results: CfrSearchResult[]): string {
        if (results.length === 0) {
            return 'No related CFR regulations found.';
        }

        const formattedResults = results.map((result, index) => {
            const section = result.section;
            const sectionRef = section
                ? `14 CFR ${section.section} - ${section.title}`
                : `Section ${result.chunk.section_id}`;
            const score = (result.score * 100).toFixed(0);

            return `${index + 1}. ${sectionRef} (${score}% relevance)
   ${result.chunk.content.substring(0, 500)}${result.chunk.content.length > 500 ? '...' : ''}`;
        }).join('\n\n');

        return `Related CFR Regulations:\n\n${formattedResults}`;
    }
}

export default new CfrService();
